"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { clog } from "@/lib/client-log";
import { markTickerEventsRead } from "../actions/ticker";
import { markChatRead } from "../actions/chat";
import type { TickerCatchUpItem } from "@/lib/ticker-types";
import type { ChatMessageView } from "@/lib/chat-types";
import ArenaTicker, { type TickerItem, type BreakingNews } from "./arena-ticker";
import { ArenaChatPanel } from "./arena-chat-panel";
import ScreenEdgeFlash from "./screen-edge-flash";
import { fetchSpriteData } from "./arena-sprite";
import type { Spectator } from "./arena-sprite";
import {
  subscribeArenaTickerContent,
  getArenaTickerContentSnapshot,
  getEmptyArenaTickerContent,
} from "@/lib/arena-ticker-content-store";

const PING_WATCHDOG_MS = 25_000; // longer than the 10s server ping; just for stale-connection detection
const FOCUS_HEARTBEAT_DIVISOR = 2; // heartbeat at timeout/2 so a missed beat doesn't expire the flag
// A heartbeat tick fires with no corresponding real interaction in this long
// → treat it as a freezing/backgrounded tab that never cleanly reported
// going hidden, and force focus:false instead of re-arming. Comfortably
// above one full heartbeat interval (7.5s) so ordinary read-without-touching
// time between taps never false-positives.
const STALE_FOCUS_MS = 45_000;

interface MentionCandidate { id: string; displayName: string; }

interface GlobalTickerClientProps {
  currentUserId: string;
  isAdmin: boolean;
  initialItems: TickerCatchUpItem[];
  initialChatMessages: ChatMessageView[];
  initialHasUnreadChat: boolean;
  initialRoster: Array<{ userId: string; displayName: string; spriteClass: string; spritePalette: string }>;
  mentionCandidates: MentionCandidate[];
}

// Site-wide ticker + chat/online panel — mounted on every page (app/layout.tsx).
// This is now the ONLY chat/online surface in the app (live-arena.tsx's old
// matchup-scoped panel was retired) — on /vote too, "Online" shows site-wide
// presence (anyone with a focused tab anywhere), not just who's watching that
// match. Owns:
//   1. The /api/site-stream SSE connection (shout/mention/ticker_notification/
//      chat_*/site_matchup_update/site_presence_*).
//   2. The focus heartbeat (POST /api/site-focus) driving lib/notify.ts's
//      push-vs-ticker decision AND the site-wide presence roster.
//   3. The breaking-news FIFO queue.
//   4. The chat/online panel (ArenaChatPanel) and its unread-glow state.
export default function GlobalTickerClient({
  currentUserId, isAdmin, initialItems, initialChatMessages, initialHasUnreadChat, initialRoster, mentionCandidates,
}: GlobalTickerClientProps) {
  // Matchup-local content (current match, standings, results/LCK items),
  // published by live-arena.tsx only while mounted on /vote — empty
  // everywhere else. See lib/arena-ticker-content-store.ts.
  //
  // getServerSnapshot is pinned to the empty default rather than the live
  // getArenaTickerContentSnapshot. The store is a plain module-level
  // variable that persists for the lifetime of the page, so by the time this
  // component (re-)hydrates — e.g. after a client-side navigation back to a
  // page that already had live-arena.tsx's effect populate the store earlier
  // in the session — the "current" snapshot can already differ from what
  // was true when the server rendered this HTML. Using the live snapshot as
  // the server snapshot let that drift become a real SSR/CSR hydration
  // mismatch. Forcing the server snapshot to the empty default guarantees the
  // first client render always matches SSR; the real value then arrives via
  // the normal useSyncExternalStore subscription right after, which is a
  // plain post-hydration update, not a mismatch.
  const arenaContent = useSyncExternalStore(subscribeArenaTickerContent, getArenaTickerContentSnapshot, getEmptyArenaTickerContent);
  const [siteMatchup, setSiteMatchup] = useState<{ matchNumber?: number; nameA?: string; nameB?: string; standings: { name: string; wins: number; losses: number }[] }>({ standings: [] });
  const [breakingNews, setBreakingNews] = useState<BreakingNews | null>(null);
  const [connStatus, setConnStatus] = useState<"connected" | "reconnecting" | "disconnected">("reconnecting");
  const [edgeFlashKey, setEdgeFlashKey] = useState(0);

  // ── Chat/online panel state ────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInitialTab, setChatInitialTab] = useState<"online" | "chat">("online");
  const [chatMessages, setChatMessages] = useState<ChatMessageView[]>(initialChatMessages);
  const [hasUnreadChat, setHasUnreadChat] = useState(initialHasUnreadChat);
  const chatOpenRef = useRef(false);
  const chatTabRef = useRef<"online" | "chat">("online");
  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);
  useEffect(() => { chatTabRef.current = chatInitialTab; }, [chatInitialTab]);

  const [spectators, setSpectators] = useState<Map<string, Spectator>>(() => {
    const m = new Map<string, Spectator>();
    for (const r of initialRoster) m.set(r.userId, { ...r, spriteUri: "" });
    return m;
  });

  // Hydrate sprites for the initial roster, same fetch live-arena.tsx's
  // upsertSpectator already uses.
  useEffect(() => {
    initialRoster.forEach(async (r) => {
      const { uri, animMeta } = await fetchSpriteData(r.userId, r.spriteClass, r.spritePalette);
      setSpectators((prev) => {
        const next = new Map(prev);
        const existing = next.get(r.userId);
        if (existing) next.set(r.userId, { ...existing, spriteUri: uri, animMeta });
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function upsertSpectator(info: { userId: string; displayName: string; spriteClass: string; spritePalette: string }) {
    const { uri, animMeta } = await fetchSpriteData(info.userId, info.spriteClass, info.spritePalette);
    setSpectators((prev) => {
      const next = new Map(prev);
      next.set(info.userId, { ...info, spriteUri: uri, animMeta });
      return next;
    });
  }

  const breakingNewsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const breakingNewsQueueRef = useRef<BreakingNews[]>([]);
  // Mirrors breakingNews for use inside the SSE onmessage closure, which is
  // bound once per connectSSE() call, not re-bound on every breakingNews
  // change — same stale-closure hazard/fix already used in live-arena.tsx
  // for chatOpenRef/breakingNewsRef.
  const breakingNewsRef = useRef<BreakingNews | null>(null);
  useEffect(() => { breakingNewsRef.current = breakingNews; }, [breakingNews]);

  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastEventTsRef = useRef(Date.now());
  const pingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Real user interaction, not just "tab reports visible" — see the focus
  // heartbeat effect below for why this exists.
  const lastInteractionRef = useRef(Date.now());

  function enqueueBreakingNews(item: BreakingNews) {
    if (breakingNewsRef.current === null) {
      setBreakingNews(item);
      if (breakingNewsTimerRef.current) clearTimeout(breakingNewsTimerRef.current);
      breakingNewsTimerRef.current = setTimeout(() => advanceBreakingNewsQueue(), 20_000);
    } else {
      breakingNewsQueueRef.current.push(item);
    }
  }

  function advanceBreakingNewsQueue() {
    const next = breakingNewsQueueRef.current.shift() ?? null;
    setBreakingNews(next);
    if (next) {
      if (breakingNewsTimerRef.current) clearTimeout(breakingNewsTimerRef.current);
      breakingNewsTimerRef.current = setTimeout(() => advanceBreakingNewsQueue(), 20_000);
    }
  }

  function fireAttentionEffects() {
    setEdgeFlashKey((k) => k + 1);
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      try { navigator.vibrate([120, 60, 120]); } catch { /* no-op */ }
    }
  }

  // ── Catch-up: enqueue once on mount, then mark read ──────────────────────
  useEffect(() => {
    if (initialItems.length === 0) return;
    for (const item of initialItems) {
      enqueueBreakingNews({ id: item.dbId, icon: item.icon, text: item.text });
    }
    markTickerEventsRead(initialItems.map((i) => i.dbId)).catch((err) =>
      clog.warn("ticker", "markTickerEventsRead failed", { error: String(err) })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Focus presence heartbeat ──────────────────────────────────────────────
  // Posts on every focus/blur transition, plus a periodic heartbeat while
  // focused (the Redis flag is TTL-based — see lib/site-focus.ts). Unfocus is
  // posted immediately rather than left to expire, so push suppression (and
  // the online roster) update the instant the user actually looks away, not
  // up to a full TTL later.
  //
  // Real-device evidence (client logs) showed the heartbeat interval can
  // keep firing {focused:true} for a while into an iOS WebKit JS-execution
  // freeze (the same already-documented freeze elsewhere in this app) —
  // visibilitychange→hidden, the ONLY thing that stops this interval, is not
  // guaranteed to fire promptly (or at all) once a page starts freezing, so
  // a tab can keep re-arming the server's focus:<userId> TTL key for longer
  // than the user is actually looking at it. Closed via lastInteractionRef:
  // a real-interaction timestamp (same signal live-arena.tsx already uses
  // for away-detection — pointerdown/keydown/scroll/touchstart), checked
  // before every heartbeat tick. If no real interaction happened within the
  // last STALE_FOCUS_MS, the tick force-clears focus instead of re-arming it
  // — independent of whether visibilitychange ever reports the tab hidden.
  useEffect(() => {
    function postFocus(focused: boolean) {
      fetch("/api/site-focus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ focused }),
      }).catch((err) => clog.warn("ticker", "site-focus POST failed", { focused, error: String(err) }));
    }

    function markInteraction() { lastInteractionRef.current = Date.now(); }
    const interactionOpts = { passive: true } as const;
    window.addEventListener("pointerdown", markInteraction, interactionOpts);
    window.addEventListener("keydown", markInteraction, interactionOpts);
    window.addEventListener("scroll", markInteraction, interactionOpts);
    window.addEventListener("touchstart", markInteraction, interactionOpts);

    function startHeartbeat() {
      if (heartbeatIntervalRef.current) return;
      markInteraction();
      postFocus(true);
      // 15s default focus_timeout_seconds / 2 — close enough without a round
      // trip to read the live SiteConfig value just for the client interval.
      heartbeatIntervalRef.current = setInterval(() => {
        if (Date.now() - lastInteractionRef.current > STALE_FOCUS_MS) {
          clog.warn("ticker", "heartbeat tick with no real interaction in a while — forcing focus:false instead of re-arming", {
            quietMs: Date.now() - lastInteractionRef.current,
          });
          stopHeartbeat();
          postFocus(false);
          return;
        }
        postFocus(true);
      }, (15_000 / FOCUS_HEARTBEAT_DIVISOR));
    }
    function stopHeartbeat() {
      if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
    }

    function handleVisibility() {
      if (document.visibilityState === "visible") { markInteraction(); startHeartbeat(); }
      else { stopHeartbeat(); postFocus(false); }
    }

    if (document.visibilityState === "visible") startHeartbeat();
    document.addEventListener("visibilitychange", handleVisibility);

    function handlePageHide() {
      if ("sendBeacon" in navigator) {
        navigator.sendBeacon("/api/site-focus", new Blob([JSON.stringify({ focused: false })], { type: "application/json" }));
      }
    }
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("keydown", markInteraction);
      window.removeEventListener("scroll", markInteraction);
      window.removeEventListener("touchstart", markInteraction);
      stopHeartbeat();
    };
  }, []);

  // ── SSE connection ────────────────────────────────────────────────────────
  useEffect(() => {
    function connect() {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }

      const es = new EventSource("/api/site-stream");
      esRef.current = es;

      es.onopen = () => {
        setConnStatus("connected");
        reconnectAttemptRef.current = 0;
        lastEventTsRef.current = Date.now();
      };

      es.onmessage = (ev) => {
        lastEventTsRef.current = Date.now();
        let parsed: { type?: string } = {};
        try { parsed = JSON.parse(ev.data); } catch { return; }

        switch (parsed.type) {
          case "shout": {
            const data = parsed as unknown as { id: string; displayName: string; body: string };
            if (chatOpenRef.current) {
              setChatMessages((prev) => [...prev, {
                id: `sys-${Date.now()}`, userId: "__system__", displayName: "", body: `📣 ${data.displayName}: ${data.body}`,
                createdAt: new Date().toISOString(), deleted: false, likeCount: 0, likedByMe: false,
              }].slice(-200));
            } else {
              enqueueBreakingNews({ id: data.id, icon: "📣", text: `${data.displayName}: ${data.body}` });
              fireAttentionEffects();
            }
            break;
          }
          case "mention": {
            const data = parsed as unknown as { targetUserId: string; fromDisplayName: string; body: string; serverTs: string };
            if (data.targetUserId !== currentUserId) break;
            if (chatOpenRef.current) {
              setChatMessages((prev) => [...prev, {
                id: `sys-${Date.now()}`, userId: "__system__", displayName: "", body: `💬 ${data.fromDisplayName} mentioned you: ${data.body}`,
                createdAt: new Date().toISOString(), deleted: false, likeCount: 0, likedByMe: false,
              }].slice(-200));
            } else {
              enqueueBreakingNews({ id: `mention-${data.serverTs}`, icon: "💬", text: `${data.fromDisplayName} mentioned you: ${data.body}` });
              fireAttentionEffects();
            }
            break;
          }
          case "ticker_notification": {
            const data = parsed as unknown as { targetUserId?: string; icon: string; text: string; serverTs: string };
            if (data.targetUserId && data.targetUserId !== currentUserId) break;
            enqueueBreakingNews({ id: `notif-${data.serverTs}`, icon: data.icon, text: data.text });
            fireAttentionEffects();
            break;
          }
          case "chat_message": {
            const data = parsed as unknown as { id: string; userId: string; displayName: string; body: string; serverTs: string };
            setChatMessages((prev) => {
              if (prev.some((m) => m.id === data.id)) return prev;
              return [...prev, {
                id: data.id, userId: data.userId, displayName: data.displayName, body: data.body,
                createdAt: data.serverTs, deleted: false, likeCount: 0, likedByMe: false,
              }].slice(-200);
            });
            // Mark unread unless the chat tab is actually being viewed right now.
            if (data.userId !== currentUserId && !(chatOpenRef.current && chatTabRef.current === "chat")) {
              setHasUnreadChat(true);
            }
            break;
          }
          case "chat_like": {
            const data = parsed as unknown as { messageId: string; likeCount: number };
            setChatMessages((prev) => prev.map((m) => m.id === data.messageId ? { ...m, likeCount: data.likeCount } : m));
            break;
          }
          case "chat_deleted": {
            const data = parsed as unknown as { messageId: string };
            setChatMessages((prev) => prev.map((m) => m.id === data.messageId ? { ...m, deleted: true, body: "" } : m));
            break;
          }
          case "site_matchup_update": {
            const data = parsed as unknown as { matchNumber?: number; nameA?: string; nameB?: string; standings: { name: string; wins: number; losses: number }[] };
            setSiteMatchup({ matchNumber: data.matchNumber, nameA: data.nameA, nameB: data.nameB, standings: data.standings });
            break;
          }
          case "site_presence_sync": {
            const data = parsed as unknown as { spectators: Array<{ userId: string; displayName: string; spriteClass: string; spritePalette: string }> };
            const ids = new Set(data.spectators.map((s) => s.userId));
            setSpectators((prev) => {
              const next = new Map<string, Spectator>();
              for (const [id, spec] of prev) if (ids.has(id)) next.set(id, spec);
              return next;
            });
            data.spectators.forEach((s) => upsertSpectator(s));
            break;
          }
          case "site_presence_update": {
            const data = parsed as unknown as { userId: string; displayName: string; spriteClass: string; spritePalette: string; online: boolean };
            if (data.userId === currentUserId) break;
            if (data.online) {
              upsertSpectator(data);
            } else {
              setSpectators((prev) => {
                if (!prev.has(data.userId)) return prev;
                const next = new Map(prev);
                next.delete(data.userId);
                return next;
              });
            }
            break;
          }
          case "maintenance_warning":
          case "maintenance_cancelled":
            // Already handled by live-arena.tsx's full-screen overlay on
            // /vote; the global ticker doesn't need its own treatment here.
            break;
          default:
            break;
        }
      };

      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          setConnStatus("reconnecting");
          scheduleReconnect();
        }
      };
    }

    function scheduleReconnect() {
      if (reconnectTimerRef.current) return;
      const attempt = reconnectAttemptRef.current++;
      const delay = Math.min(1000 * 2 ** attempt, 30_000);
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    }

    connect();

    // Stale-connection watchdog — no time-sensitive deadline math here (no
    // trophy countdown equivalent), so a simple staleness check is enough;
    // doesn't need live-arena.tsx's epoch-guarded reconnect complexity.
    pingWatchdogRef.current = setInterval(() => {
      if (Date.now() - lastEventTsRef.current > PING_WATCHDOG_MS && esRef.current?.readyState !== EventSource.CONNECTING) {
        setConnStatus("reconnecting");
        scheduleReconnect();
      }
    }, 5_000);

    return () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingWatchdogRef.current) clearInterval(pingWatchdogRef.current);
      if (breakingNewsTimerRef.current) clearTimeout(breakingNewsTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  function openChat(tab: "online" | "chat") {
    setChatInitialTab(tab);
    setChatOpen(true);
    if (tab === "chat") {
      setHasUnreadChat(false);
      markChatRead().catch((err) => clog.warn("chat", "markChatRead failed", { error: String(err) }));
    }
  }

  // One ticker, everywhere — merges this component's own global content
  // (shouts, mentions, push-notification catch-up, all surfaced via
  // breakingNews) with live-arena.tsx's matchup-local content (current
  // match/standings/results, only non-empty while mounted on /vote) and,
  // when that's empty, this component's own site-wide matchup feed (current
  // match/standings, for every other page).
  const hasArenaContent = arenaContent.matchNumber !== undefined || arenaContent.standings.length > 0 || arenaContent.items.length > 0;
  const mergedItems = arenaContent.items;
  const effectiveConnStatus = hasArenaContent ? arenaContent.connStatus : connStatus;
  const effectiveMatchNumber = hasArenaContent ? arenaContent.matchNumber : siteMatchup.matchNumber;
  const effectiveNameA = hasArenaContent ? arenaContent.nameA : siteMatchup.nameA;
  const effectiveNameB = hasArenaContent ? arenaContent.nameB : siteMatchup.nameB;
  const effectiveStandings = hasArenaContent ? arenaContent.standings : siteMatchup.standings;

  return (
    <>
      <ScreenEdgeFlash triggerKey={edgeFlashKey} />
      {/* Safe-area-bottom clearance is reserved on #app-shell itself (see
          globals.css) so the ticker always lands fully inside the real,
          touchable 100dvh box instead of overflowing past it in an iOS
          standalone PWA — don't re-add the inset here. */}
      <div className="shrink-0" style={{ background: "#0a0318" }}>
        <ArenaTicker
          items={mergedItems}
          matchNumber={effectiveMatchNumber}
          nameA={effectiveNameA}
          nameB={effectiveNameB}
          standings={effectiveStandings}
          connStatus={effectiveConnStatus}
          breakingNews={breakingNews}
          onBreakingNewsDuration={(totalMs) => {
            if (breakingNewsTimerRef.current) clearTimeout(breakingNewsTimerRef.current);
            breakingNewsTimerRef.current = setTimeout(() => advanceBreakingNewsQueue(), totalMs);
          }}
          hasUnreadChat={hasUnreadChat}
          onChatClick={() => openChat("chat")}
        />
      </div>
      {chatOpen && (
        <ArenaChatPanel
          spectators={Array.from(spectators.values())}
          messages={chatMessages}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          mentionCandidates={mentionCandidates}
          initialTab={chatInitialTab}
          connStatus={effectiveConnStatus}
          onChatTabViewed={() => {
            setHasUnreadChat(false);
            markChatRead().catch((err) => clog.warn("chat", "markChatRead failed", { error: String(err) }));
          }}
          onClose={() => setChatOpen(false)}
        />
      )}
    </>
  );
}
